/** SettingsRow / Toggle / RadioGroup primitives (06 §4.18). */
import type { ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';
import styles from './SettingsControls.module.css';

export function SettingsRow({
  icon,
  label,
  value,
  onClick,
  trailing,
}: {
  icon?: ReactNode;
  label: string;
  value?: string;
  onClick?: () => void;
  trailing?: ReactNode;
}) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag className={styles.row} onClick={onClick}>
      {icon && <span className={styles.icon}>{icon}</span>}
      <span className={styles.labelWrap}>
        <span className={styles.label}>{label}</span>
        {value && <span className={styles.value}>{value}</span>}
      </span>
      {trailing ?? (onClick ? <ChevronRight size={18} className={styles.chevron} /> : null)}
    </Tag>
  );
}

export function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <button
      role="switch"
      aria-checked={checked}
      aria-label={label}
      className={`${styles.toggle} ${checked ? styles.on : ''}`}
      onClick={() => onChange(!checked)}
    >
      <span className={styles.knob} />
    </button>
  );
}

export function RadioGroup<T extends string>({
  options,
  value,
  onChange,
  name,
}: {
  options: { value: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
  name: string;
}) {
  return (
    <div role="radiogroup" aria-label={name} className={styles.radioGroup}>
      {options.map((o) => (
        <button
          key={o.value}
          role="radio"
          aria-checked={value === o.value}
          className={styles.radioRow}
          onClick={() => onChange(o.value)}
        >
          <span className={`${styles.radio} ${value === o.value ? styles.radioOn : ''}`} />
          {o.label}
        </button>
      ))}
    </div>
  );
}
